import { jsxs, jsx } from 'react/jsx-runtime';
import * as React from 'react';
import '../../utils/index_orig/index.esm.js';
import '../../utils/theme/theme-provider/index.esm.js';
import '../../layout-flex/types/index.esm.js';
import { EGDSLayoutFlex } from '../../layout-flex/layout-flex/index.esm.js';
import { EGDSLayoutFlexItem } from '../../layout-flex/layout-flex-item/index.esm.js';
import { useLocalization } from '@shared-ui/localization-context';
import { attachHandlers, setElementFocus, hasSheetClassName, detachHandlers, setFocus, updateSheetProps, defineSheetLayout, trapUpperBoundFocus, trapLowerBoundFocus } from '../sheet-utilities/index.esm.js';
import styles from '../../styles/uitk-egds-sheet.css';
import { classNames } from '../../utils/classnames/index.esm.js';
import { liveAnnounce, Level } from '../../utils/live-announce/index.esm.js';
const getClassNameList = classNames.bind(styles);
const EGDSSideSheet = props => {
  const {
    ariaLabel,
    autoFocus = true,
    children,
    className,
    hasModal,
    isVisible,
    onDismiss,
    overflow,
    returnFocusOnClose = true,
    sideSheetLayout,
    triggerRef,
    focusElemRef,
    type = 'side',
    ...attributes
  } = props;
  const sheetRef = React.useRef(null);
  const baseClassName = `uitk-${type}-sheet`;
  const propsRef = React.useRef({
    onDismiss,
    triggerRef,
    hasModal
  });
  // Hooks
  // Set focus when initial mount only
  React.useEffect(() => {
    if (isVisible) {
      attachHandlers(props, sheetRef);
      // Reset liveannounce so the liveannounce element is not navigable by screenreader.
      liveAnnounce('', Level.POLITE);
      if (autoFocus) {
        setElementFocus(sheetRef, hasModal, focusElemRef);
      }
    }
    hasModal && hasSheetClassName('add');
  }, [autoFocus, hasModal, isVisible]);
  // "unmount" hook
  React.useEffect(() => () => {
    if (!isVisible) {
      detachHandlers(sheetRef);
    }
    if (returnFocusOnClose) {
      setFocus(triggerRef);
    }
  }, [returnFocusOnClose, triggerRef]);
  // Detach handlers on unmount
  React.useEffect(() => () => {
    detachHandlers(sheetRef);
    hasModal && hasSheetClassName('remove');
  }, []);
  // Update sheet reference props
  React.useEffect(() => {
    propsRef.current = {
      onDismiss,
      triggerRef,
      hasModal
    };
    updateSheetProps(sheetRef, propsRef.current);
  }, [onDismiss, triggerRef, hasModal]);
  const {
    hasCloseButton,
    layoutProps,
    closeButtonLayoutProps,
    closeButtonElem,
    childrenElem,
    footerElem
  } = defineSheetLayout(children);
  const layoutClasses = (obj, cssBaseClass) => {
    const layoutObj = obj;
    if (!('position' in obj)) {
      layoutObj.position = 'trailing'; // Set 'trailing' default
    }
    const str = Object.entries(layoutObj).map(([key, val]) => `${cssBaseClass}-${key}-${val}`).join(' ');
    return str;
  };
  const sheetClassNames = getClassNameList(baseClassName, sideSheetLayout && layoutClasses(sideSheetLayout, baseClassName), {
    [`${baseClassName}-position-trailing`]: !sideSheetLayout,
    [`${baseClassName}-has-overflow`]: overflow
  }, className);
  const l10n = useLocalization();
  return jsxs("section", {
    ref: sheetRef,
    className: sheetClassNames,
    tabIndex: -1,
    role: "dialog",
    "aria-label": ariaLabel ?? l10n.formatText('sheets.rootElement.ariaLabel'),
    ...attributes,
    children: [hasModal && jsx("div", {
      tabIndex: 0,
      onFocus: trapUpperBoundFocus
    }), hasCloseButton && jsxs(EGDSLayoutFlex, {
      ...layoutProps,
      children: [jsx(EGDSLayoutFlexItem, {
        ...closeButtonLayoutProps,
        children: closeButtonElem
      }), jsx(EGDSLayoutFlexItem, {
        children: childrenElem
      })]
    }), !hasCloseButton && childrenElem, footerElem, hasModal && jsx("div", {
      tabIndex: 0,
      onFocus: trapLowerBoundFocus
    })]
  });
};
EGDSSideSheet.displayName = 'EGDSSideSheet';
export { EGDSSideSheet };